import { activityCategoryLabels, RegionId, TaskTypeId, TouchpointId } from "@sp-crm/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { goalMetricItemHref } from "components/marketing-goals/goal-metric-item";
import { trpc } from "components/rpc/trpc";
import { ActivityChip } from "components/tasks/activity-chip";
import { activityTypeName } from "components/tasks/activity-type-name";
import {
    activityTypeChoices,
    ActivityTypeOption,
    chooseActivityTypeLabel,
    noActivityTypeKey,
    useActivityTypeOptions,
} from "components/tasks/activity-type-options";
import { EditButton } from "components/ui/action-button";
import { InteractiveTextLink } from "components/ui/interactive-text-link";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useNow, useRegionId } from "store/selectors/hooks";
import {
    describeActivity,
    formatActivityTime,
    groupActivityByDate,
    MyActivityItem,
    trailingMonthRange,
} from "./my-activity";

const dayLabel = (date: string): string =>
    new Date(`${date}T00:00:00.000Z`).toLocaleDateString("en-US", {
        weekday: "long",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
    });

export const MyActivityTab: React.FC<unknown> = () => {
    const now = useNow();
    const regionId = useRegionId();
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const input = {
        ...trailingMonthRange(now),
        regionId: regionId ?? ("" as RegionId),
    };
    const activityTypes = useActivityTypeOptions();

    const activityQuery = useQuery({
        ...trpcClient.touchpoint.myActivity.queryOptions(input),
        enabled: !!regionId,
    });

    const onActivityTypeSaved = () =>
        queryClient.invalidateQueries({
            queryKey: trpcClient.touchpoint.myActivity.queryKey(input),
        });

    const items: MyActivityItem[] = (activityQuery.data ?? []).map(row => ({
        id: row.id,
        category: row.category,
        taskTypeId: row.taskTypeId ?? null,
        date: row.date,
        time: row.time,
        parentName: row.parentName ?? null,
        link: row.link ?? null,
        source: row.source ?? null,
    }));
    const days = groupActivityByDate(items);

    if (activityQuery.isLoading) {
        return (
            <div className="flex justify-center rounded-lg bg-white p-12 ring-1 ring-gray-300">
                <Spinner />
            </div>
        );
    }

    if (days.length === 0) {
        return (
            <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-500 ring-1 ring-gray-300">
                No activity recorded in the last 30 days. Emails, calls, texts, visits,
                and completed activities show up here.
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <p className="text-sm text-gray-500">
                Everything you logged in the last 30 days.
            </p>
            {days.map(day => (
                <section
                    key={day.date}
                    className="rounded-lg bg-white ring-1 ring-gray-300">
                    <h3 className="border-b border-gray-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {dayLabel(day.date)}
                    </h3>
                    <div className="divide-y divide-gray-100">
                        {day.items.map(item => (
                            <MyActivityRow
                                key={item.id}
                                item={item}
                                activityTypes={activityTypes}
                                onSaved={onActivityTypeSaved}
                            />
                        ))}
                    </div>
                </section>
            ))}
        </div>
    );
};

const MyActivityRow: React.FC<{
    item: MyActivityItem;
    activityTypes: ActivityTypeOption[];
    onSaved: () => Promise<void>;
}> = ({ item, activityTypes, onSaved }) => {
    const presentation = describeActivity(item);
    return (
        <div className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-start sm:gap-3">
            <div className="flex min-w-0 flex-1 items-start gap-3">
                <div className="flex-shrink-0 sm:w-24">
                    <ActivityChip
                        category={item.category}
                        label={activityCategoryLabels[item.category]}
                    />
                </div>
                <div className="min-w-0 flex-1">
                    {presentation.summary ? (
                        <div className="truncate text-sm text-gray-900">
                            {presentation.summary}
                        </div>
                    ) : null}
                    <div className="truncate text-xs text-gray-500">
                        {item.link && item.parentName ? (
                            <Link
                                to={goalMetricItemHref(item.link)}
                                className="text-brand-700 hover:underline">
                                {item.parentName}
                            </Link>
                        ) : (
                            (item.parentName ?? "—")
                        )}
                    </div>
                    {presentation.outcome ? (
                        <div className="mt-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs text-emerald-900">
                            <span className="font-semibold">Outcome: </span>
                            {presentation.outcome}
                        </div>
                    ) : null}
                </div>
            </div>
            <div className="flex flex-shrink-0 items-center gap-2 sm:w-64 sm:justify-end">
                <ActivityTypeCell
                    item={item}
                    activityTypes={activityTypes}
                    onSaved={onSaved}
                />
                <span className="ml-auto w-16 flex-shrink-0 text-right text-[11px] tabular-nums text-gray-400 sm:ml-0">
                    {formatActivityTime(item.time)}
                </span>
            </div>
        </div>
    );
};

const ActivityTypeCell: React.FC<{
    item: MyActivityItem;
    activityTypes: ActivityTypeOption[];
    onSaved: () => Promise<void>;
}> = ({ item, activityTypes, onSaved }) => {
    const trpcClient = trpc.useTRPC();
    const [isEditing, setIsEditing] = useState(false);
    const [chosenTypeId, setChosenTypeId] = useState<TaskTypeId | null | undefined>(
        undefined,
    );
    const setActivityType = useMutation({
        ...trpcClient.touchpoint.setActivityType.mutationOptions(),
        onSuccess: async () => {
            await onSaved();
            setChosenTypeId(undefined);
        },
    });
    const startEditing = useCallback(() => setIsEditing(true), []);
    const stopEditing = useCallback(() => setIsEditing(false), []);
    const chooseType = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const activityTypeId =
                e.target.value === noActivityTypeKey
                    ? null
                    : (e.target.value as TaskTypeId);
            setChosenTypeId(activityTypeId);
            setIsEditing(false);
            setActivityType.mutate({
                touchpointId: item.id as TouchpointId,
                activityTypeId,
            });
        },
        [item.id, setActivityType],
    );

    const selectedTypeId = chosenTypeId === undefined ? item.taskTypeId : chosenTypeId;
    const choices = activityTypeChoices(activityTypes, item.category, selectedTypeId);
    const name = activityTypeName(activityTypes, selectedTypeId);

    if (isEditing) {
        return (
            <Select
                label=""
                textSize="sm"
                className="min-w-0 flex-1"
                autoFocus
                value={selectedTypeId ?? noActivityTypeKey}
                onChange={chooseType}
                onBlur={stopEditing}>
                {selectedTypeId ? null : (
                    <option value={noActivityTypeKey}>{chooseActivityTypeLabel}</option>
                )}
                {choices.map(choice => (
                    <option key={choice.id} value={choice.id}>
                        {choice.name}
                    </option>
                ))}
            </Select>
        );
    }

    if (name) {
        return (
            <div className="flex min-w-0 items-center gap-1">
                <span className="truncate rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">
                    {name}
                </span>
                {choices.length === 0 ? null : (
                    <EditButton
                        backgroundColor="bg-white"
                        size="sm"
                        onClick={startEditing}
                    />
                )}
            </div>
        );
    }

    if (choices.length === 0) {
        return null;
    }

    return (
        <div className="flex min-w-0 items-center gap-1">
            <InteractiveTextLink className="truncate text-xs" onClick={startEditing}>
                Set a type
            </InteractiveTextLink>
            <EditButton backgroundColor="bg-white" size="sm" onClick={startEditing} />
        </div>
    );
};
